import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { WarehousingIncomingClaim, WarehousingItem, WarehousingMasterData, WarehousingSupplyChoice,
  WarehousingSupplyLineOptions, WarehousingSupplyPreview } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel } from '../warehousing-item-display';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';

interface DraftChoice { lineId: number; key?: string; quantity: number; }

@Component({
  selector: 'app-warehousing-supply-reserve',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TagModule],
  templateUrl: './warehousing-supply-reserve.component.html',
  styleUrl: './warehousing-supply-reserve.component.css'
})
export class WarehousingSupplyReserveComponent implements OnInit {
  @Input({ required: true }) documentType!: 'DISPATCH' | 'TRANSFER';
  @Input({ required: true }) documentId!: number;
  @Input() items: WarehousingItem[] = [];
  @Input() zones: WarehousingMasterData[] = [];
  @Input() conditions: WarehousingMasterData[] = [];
  @Input() canEdit = false;
  @Input() autoOpen = false;
  @Output() changed = new EventEmitter<void>();
  private service = inject(WarehousingService);
  private permissions = inject(PermissionService);

  visible = false;
  busy = false;
  error = '';
  lines: WarehousingSupplyLineOptions[] = [];
  claims: WarehousingIncomingClaim[] = [];
  drafts: DraftChoice[] = [];
  preview?: WarehousingSupplyPreview;
  reason = '';
  releaseReason = '';
  private commitKey = '';

  ngOnInit(): void { if (this.autoOpen) this.open(); }
  canManage(): boolean {
    return this.canEdit && this.permissions.hasPermission(this.documentType === 'TRANSFER'
      ? app_permissions.warehousing.transfer.release : app_permissions.warehousing.dispatch.release);
  }

  open(): void {
    this.visible = true;
    this.preview = undefined;
    this.error = '';
    this.load();
  }

  load(): void {
    if (!this.documentId) return;
    this.busy = true;
    forkJoin({
      options: this.service.getOutboundSupplyOptions(this.documentType, this.documentId),
      claims: this.service.getOutboundSupplyClaims(this.documentType, this.documentId)
    }).subscribe({
      next: response => {
        this.lines = response.options.data || [];
        this.claims = response.claims.data || [];
        this.drafts = this.lines.filter(line => line.remainingQuantity > 0)
          .map(line => ({ lineId: line.lineId, quantity: line.remainingQuantity }));
        this.busy = false;
      },
      error: error => { this.error = this.message(error, 'Could not load supply options.'); this.busy = false; }
    });
  }

  itemLabel(id: number): string { return itemIdentityLabel(this.items.find(item => item.id === id)); }
  locationLabel(id: number | undefined, values: WarehousingMasterData[], fallback: string): string {
    return id == null ? fallback : values.find(value => value.id === id)?.name || `#${id}`;
  }
  line(id: number): WarehousingSupplyLineOptions | undefined { return this.lines.find(row => row.lineId === id); }
  sourceOptions(id: number): Array<{ label: string; key: string }> {
    return (this.line(id)?.options || []).map(option => ({
      key: `${option.sourceType}:${option.sourceId}`,
      label: `${option.sourceType === 'ON_HAND' ? 'On hand' : 'Incoming'} · ${option.reference}`
        + `${option.unitCode ? ' · ' + option.unitCode : ''}`
        + ` · ${this.locationLabel(option.zoneId, this.zones, 'Zone assigned on receipt')}`
        + ` / ${this.locationLabel(option.conditionId, this.conditions, 'Condition pending')}`
        + ` · ${option.availableQuantity} base available`
        + `${option.scheduledDate ? ' · scheduled ' + option.scheduledDate : ''}`
    }));
  }

  add(lineId: number): void { this.drafts.push({ lineId, quantity: 0 }); this.preview = undefined; }
  remove(draft: DraftChoice): void { this.drafts = this.drafts.filter(row => row !== draft); this.preview = undefined; }
  selectedTotal(lineId: number): number {
    return this.drafts.filter(row => row.lineId === lineId).reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  }
  hasActiveClaims(): boolean { return this.claims.some(claim => claim.status !== 'RELEASED'); }
  activeClaimCount(): number { return this.claims.filter(claim => claim.status !== 'RELEASED').length; }
  canReview(): boolean {
    return this.canManage() && !this.hasActiveClaims()
      && this.drafts.length > 0 && this.drafts.every(row => !!row.key && row.quantity > 0)
      && this.lines.every(line => Math.abs(this.selectedTotal(line.lineId) - line.remainingQuantity) < 0.0001);
  }

  private choices(): WarehousingSupplyChoice[] {
    return this.drafts.map(row => {
      const [sourceType, sourceId] = (row.key || '').split(':');
      return { lineId: row.lineId, sourceType: sourceType as WarehousingSupplyChoice['sourceType'],
        sourceId: Number(sourceId), quantity: Number(row.quantity) };
    });
  }

  review(): void {
    if (!this.canReview() || this.busy) return;
    this.busy = true;
    this.error = '';
    this.service.previewOutboundSupply(this.documentType, this.documentId, this.choices()).subscribe({
      next: response => { this.preview = response.data; this.commitKey = crypto.randomUUID(); this.busy = false; },
      error: error => { this.error = this.message(error, 'Supply could not be reviewed.'); this.busy = false; }
    });
  }

  confirm(): void {
    if (!this.preview || !this.reason.trim() || this.busy) return;
    this.busy = true;
    this.service.confirmOutboundSupply(this.preview.token, this.reason.trim(), this.commitKey).subscribe({
      next: () => { this.busy = false; this.preview = undefined; this.changed.emit(); this.load(); },
      error: error => { this.error = this.message(error, 'Supply changed. Review it again.'); this.preview = undefined; this.busy = false; }
    });
  }

  release(claim: WarehousingIncomingClaim): void {
    if (!this.releaseReason.trim() || this.busy) return;
    this.busy = true;
    this.service.releaseOutboundSupplyClaim(claim.id, this.releaseReason.trim()).subscribe({
      next: () => { this.busy = false; this.releaseReason = ''; this.changed.emit(); this.load(); },
      error: error => { this.error = this.message(error, 'Claim could not be released.'); this.busy = false; }
    });
  }

  private message(error: any, fallback: string): string {
    return error?.error?.message || error?.error?.error || fallback;
  }
}
